import { useState } from 'react';
import { Button } from '../ui/button';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminResetButton - Admin-only button to reset R-PAT progress for testing
 * Allows admins to start fresh and test the entire flow
 */
const AdminResetButton = ({ api, onReset }) => {
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    try {
      setResetting(true);
      await api.post('/rpat/admin/reset-progress');
      toast.success('R-PAT progress has been reset. You can now test the entire flow from the beginning.');
      if (onReset) {
        onReset();
      }
    } catch (err) {
      console.error('Failed to reset progress:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset progress');
    } finally {
      setResetting(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="border-red-200 text-red-600 hover:bg-red-50"
          data-testid="admin-reset-rpat-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Reset R-PAT Progress
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset R-PAT Progress?</AlertDialogTitle>
          <AlertDialogDescription>
            This will reset ALL your R-PAT progress. This action cannot be undone. Use this for testing purposes only.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="my-4 px-1">
          <p className="text-sm text-muted-foreground mb-2">Items that will be reset:</p>
          <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
            <li>All completed topics and lessons</li>
            <li>Assessment scores and typing test results</li>
            <li>Phase 5-8 certification progress</li>
            <li>Job category selection</li>
            <li>Certificate (if earned)</li>
          </ul>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleReset}
            className="bg-red-600 hover:bg-red-700"
            disabled={resetting}
          >
            {resetting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset Everything
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AdminResetButton;
